import type { ReactNode } from 'react'

interface Props {
  title: string
  /** Optional supporting line under the title. */
  subtitle?: string
  action?: ReactNode
  children: ReactNode
}

/**
 * Standard screen frame: header plus content well.
 *
 * Deliberately NOT safe-area-aware. `AppShell` clears the notch once, on
 * `<main>`, for every route. A `pt-safe` here was a second full inset stacked
 * in the same vertical run — 118px of dead space above the heading on a
 * 59px-inset iPhone, to clear a 59px notch.
 *
 * `action` sits opposite the title on the heading line; the screens under the
 * shell pass the admin lock control there.
 */
export function Screen({ title, subtitle, action, children }: Props) {
  return (
    <div>
      <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {subtitle !== undefined && (
            <p className="mt-1 text-[0.9375rem] text-[var(--text-secondary)]">{subtitle}</p>
          )}
        </div>
        {action}
      </header>
      <div className="px-5 py-4">{children}</div>
    </div>
  )
}
